<template>
  <div
    class="min-h-screen bg-[#1F1F1F] text-white pt-24"
    style="background: var(--cart-bg); color: var(--on-surface)"
  >
    <div class="max-w-4xl mx-auto px-4 py-8">
      <!-- Header -->
      <div v-if="cart.length > 0" class="text-center mb-8">
        <h1
          class="text-3xl font-bold text-[#F5BE32] mb-2"
          style="color: var(--primary)"
        >
          {{ $t("cartPage.title") }}
        </h1>
        <p class="text-gray-400" style="color: var(--muted)">
          {{ $t("cartPage.subtitle") }}
        </p>
      </div>

      <!-- Cart Content -->
      <div
        v-if="cart.length === 0"
        class="flex-1 flex flex-col items-center justify-center mt-0"
      >
        <div class="flex flex-col items-center w-full max-w-lg mx-4 relative">
          <!-- Cart SVG Illustration -->
          <div class="mb-2 flex flex-col items-center mt-2">
            <img
              src="@/assets/Order/empty-cart.png"
              alt="Empty Cart"
              class="w-80 h-80 object-contain mx-auto"
            />
          </div>
          <!-- Empty Cart Message -->
          <div class="text-center px-4 flex flex-col items-center">
            <div
              class="mb-3 text-white text-center"
              style="
                color: var(--on-surface);
                font-size: 20px;
                font-weight: 400;
                letter-spacing: 6px;
                text-transform: capitalize;
              "
            >
              {{ $t("cartPage.emptyCart.ohNo") }}
            </div>
            <div
              class="mb-4 text-center text-[#F3D382]"
              style="
                color: var(--primary-200);
                font-family: 'DM Serif Display', serif;
                font-size: 40px;
                font-weight: 400;
                text-transform: capitalize;
              "
            >
              {{ $t("cartPage.emptyCart.title") }}
            </div>
            <div
              class="mb-8 text-center"
              style="
                color: var(--muted);
                font-family: 'DM Sans', sans-serif;
                font-size: 20px;
                font-weight: 400;
              "
            >
              {{ $t("cartPage.emptyCart.description") }}
            </div>
            <router-link to="/menu">
              <button
                class="mt-4 flex justify-center items-center w-[412px] h-[52px] px-3 py-4 gap-3 rounded-none border-2 border-[#F3D382] text-[#232323] font-bold tracking-wider transition bg-[#F3D382] hover:bg-yellow-400 hover:text-black"
                style="
                  border-color: var(--primary-200);
                  color: var(--on-primary);
                  background: var(--primary-200);
                  font-family: 'Cormorant SC', serif;
                  font-size: 20px;
                  font-weight: 700;
                "
              >
                {{ $t("cartPage.emptyCart.goToMenu") }}
              </button>
            </router-link>
          </div>
        </div>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <!-- Cart Items -->
        <div class="lg:col-span-3">
          <div
            class="bg-[#232323] rounded-lg p-6"
            style="background: var(--surface)"
          >
            <h2 class="text-xl font-bold mb-6" style="color: var(--on-surface)">
              {{ $t("cartPage.cartItems") }} ({{ cartItemCount }})
            </h2>

            <div class="space-y-4">
              <div
                v-for="(item, index) in cart"
                :key="index"
                class="bg-[#1A1A1A] rounded-lg p-4"
                style="background: var(--cart-item-bg)"
              >
                <!-- Item Header -->
                <div class="flex justify-between items-start mb-3">
                  <h3
                    class="text-white font-semibold text-lg"
                    style="color: var(--cart-item-text)"
                  >
                    {{ getTranslatedDishName(item.name, t) }}
                  </h3>
                  <button
                    @click="removeFromCart(index)"
                    class="text-gray-400 hover:text-red-400"
                    style="color: var(--cart-item-muted)"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      ></path>
                    </svg>
                  </button>
                </div>

                <!-- Quantity Controls -->
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-3">
                    <button
                      @click="decreaseQuantity(index)"
                      class="w-8 h-8 rounded-full bg-[#F5BE32] text-black flex items-center justify-center font-bold hover:bg-yellow-400 transition"
                      style="
                        background: var(--primary);
                        color: var(--on-primary);
                      "
                    >
                      -
                    </button>
                    <span
                      class="text-white font-semibold text-lg"
                      style="color: var(--on-surface)"
                      >{{ item.quantity }}</span
                    >
                    <button
                      @click="increaseQuantity(index)"
                      class="w-8 h-8 rounded-full bg-[#F5BE32] text-black flex items-center justify-center font-bold hover:bg-yellow-400 transition"
                      style="
                        background: var(--primary);
                        color: var(--on-primary);
                      "
                    >
                      +
                    </button>
                  </div>
                  <span
                    class="text-white font-semibold text-lg"
                    style="color: var(--on-surface)"
                    >{{
                      $t("cartPage.currency", {
                        amount: (item.price * item.quantity).toFixed(2),
                      })
                    }}</span
                  >
                </div>

                <!-- Special Request -->
                <div class="space-y-2">
                  <button
                    @click="toggleSpecialRequest(index)"
                    class="text-[#F5BE32] text-sm hover:underline"
                    style="color: var(--primary)"
                  >
                    {{ $t("cartPage.specialRequest") }}
                  </button>

                  <div v-if="item.showSpecialRequest" class="mt-2">
                    <textarea
                      v-model="item.specialRequest"
                      @input="updateSpecialRequest(index, $event.target.value)"
                      :placeholder="$t('cartPage.specialRequestPlaceholder')"
                      class="w-full bg-[#181818] text-white px-3 py-2 rounded-md border focus:outline-none focus:border-[var(--primary-200)] resize-none text-sm"
                      style="
                        background: var(--cart-input-bg);
                        color: var(--cart-input-text);
                        border-color: var(--cart-border);
                      "
                      rows="2"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Summary -->
        <div class="lg:col-span-2">
          <div
            class="bg-[#232323] rounded-lg p-6 sticky top-32"
            style="background: var(--surface)"
          >
            <h2 class="text-xl font-bold mb-6" style="color: var(--on-surface)">
              {{ $t("cartPage.orderSummary") }}
            </h2>

            <!-- Cost Breakdown -->
            <div class="space-y-3 mb-6">
              <div
                class="flex justify-between text-gray-300"
                style="color: var(--muted)"
              >
                <span>{{ $t("cartPage.subtotal") }}:</span>
                <span>{{
                  $t("cartPage.currency", { amount: subtotal.toFixed(2) })
                }}</span>
              </div>
              <div
                class="flex justify-between text-gray-300"
                style="color: var(--muted)"
              >
                <span>{{ $t("cartPage.serviceFee") }}:</span>
                <span>{{
                  $t("cartPage.currency", { amount: serviceFee.toFixed(2) })
                }}</span>
              </div>
              <div
                v-if="!isFromPreorder"
                class="flex justify-between text-gray-300"
                style="color: var(--muted)"
              >
                <span>{{ $t("cartPage.deliveryFee") }}:</span>
                <span class="text-sm">{{
                  $t("cartPage.calculatedAtCheckout")
                }}</span>
              </div>
              <div
                v-if="isFromPreorder"
                class="flex justify-between text-gray-300"
                style="color: var(--muted)"
              >
                <span>{{ $t("cartPage.grandTotal") }}:</span>
                <span
                  class="font-semibold text-[#F5BE32]"
                  style="color: var(--primary)"
                  >{{
                    $t("cartPage.currency", {
                      amount: (subtotal + serviceFee).toFixed(2),
                    })
                  }}</span
                >
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="space-y-3">
              <button
                @click="handleCheckout"
                :disabled="cart.length === 0"
                class="w-full bg-[#F5BE32] text-black font-bold py-4 rounded-lg flex items-center justify-center gap-2 hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                style="background: var(--primary); color: var(--on-primary)"
              >
                {{
                  isFromPreorder
                    ? $t("cartPage.continue")
                    : $t("cartPage.checkout")
                }}
              </button>

              <router-link
  to="/menu"
  class="block w-full bg-transparent border-2 font-bold py-4 rounded-lg text-center transition
         text-[var(--primary)] border-[var(--primary)]
         hover:bg-[var(--primary)] hover:text-[var(--button-hover-text)]"
>
  {{ $t('cartPage.continueShopping') }}
</router-link>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { getFirestore, doc, updateDoc } from "firebase/firestore";
import { getTranslatedDishName } from "@/utils/dishTranslation";
import { getAuth } from "firebase/auth";

const { t } = useI18n();

const router = useRouter();
const db = getFirestore();

const cart = ref([]);
const isFromPreorder = ref(false);

onMounted(() => {
  // Load cart from localStorage
  const storedCart = localStorage.getItem("cart");
  if (storedCart) {
    cart.value = JSON.parse(storedCart);
  }

  // Check if coming from preorder
  isFromPreorder.value = localStorage.getItem("reservationId") !== null;
});

// Watch for language changes and update cart item names
watch(
  () => t("cartPage.title"),
  () => {
    // This will trigger when language changes, ensuring dish names are updated
    // The getTranslatedDishName function will handle the translation automatically
    console.log("Language changed, cart should update automatically");
  }
);

// Also watch for currency changes specifically
watch(
  () => t("cartPage.currency"),
  () => {
    console.log("Currency format changed");
  }
);

// Watch for i18n locale changes and update cart item names (same logic as Menu.vue)
watch(
  () => t("menu.title"),
  (newValue, oldValue) => {
    if (newValue !== oldValue && cart.value.length > 0) {
      // Get all menu items in current language
      const allDishes = [
        // Appetizers
        { name: t("menu.items.tomatoToast.name"), price: 120 },
        { name: t("menu.items.noodleSoup.name"), price: 90 },
        { name: t("menu.items.pumpkinSoup.name"), price: 90 },

        // Sushi
        { name: t("menu.items.dragonSushi.name"), price: 480 },
        { name: t("menu.items.creamySushi.name"), price: 250 },
        { name: t("menu.items.rollSalmonSushi.name"), price: 360 },

        // Steak
        { name: t("menu.items.steakTartare.name"), price: 850 },
        { name: t("menu.items.surfTurf.name"), price: 700 },
        { name: t("menu.items.wagyuBeef.name"), price: 950 },

        // Chicken
        { name: t("menu.items.creamyParmesan.name"), price: 260 },
        { name: t("menu.items.springChicken.name"), price: 300 },
        { name: t("menu.items.chickenFrancese.name"), price: 400 },

        // Beverages
        { name: t("menu.items.panthouseTonic.name"), price: 70 },
        { name: t("menu.items.appleBreeze.name"), price: 70 },
        { name: t("menu.items.frenchmanBlitz.name"), price: 70 },
      ];

      // Update cart item names to match the new language
      cart.value.forEach((item) => {
        // Try to find a matching dish by price (since names change with language)
        const matchingDish = allDishes.find(
          (dish) => dish.price === item.price
        );
        if (matchingDish) {
          item.name = matchingDish.name;
        }
      });

      // Save updated cart to localStorage
      saveCartToStorage();
      console.log("Cart item names updated for new language");
    }
  }
);

const cartItemCount = computed(() => {
  return cart.value.reduce((sum, item) => sum + item.quantity, 0);
});

const subtotal = computed(() => {
  return cart.value.reduce((sum, item) => sum + item.price * item.quantity, 0);
});

const serviceFee = computed(() => {
  return subtotal.value * 0.15; // 15% service fee
});

const toggleSpecialRequest = (index) => {
  cart.value[index].showSpecialRequest = !cart.value[index].showSpecialRequest;
  saveCartToStorage();
};

const updateSpecialRequest = (index, value) => {
  cart.value[index].specialRequest = value;
  saveCartToStorage();
};

const increaseQuantity = (index) => {
  cart.value[index].quantity += 1;
  saveCartToStorage();
};

const decreaseQuantity = (index) => {
  if (cart.value[index].quantity > 1) {
    cart.value[index].quantity -= 1;
  } else {
    cart.value.splice(index, 1);
  }
  saveCartToStorage();
};

const removeFromCart = (index) => {
  cart.value.splice(index, 1);
  saveCartToStorage();
};

const saveCartToStorage = () => {
  localStorage.setItem("cart", JSON.stringify(cart.value));
};

const handleCheckout = async () => {
  if (cart.value.length > 0) {
    if (isFromPreorder.value) {
      try {
        const reservationId = localStorage.getItem("reservationId");

        if (reservationId) {
          // Prepare order data
          const orderItems = cart.value.map((item) => ({
            dishName: getTranslatedDishName(item.name, t),
            count: item.quantity,
            specialRequest: item.specialRequest || "none",
          }));

          // Update reservation with order details
          const reservationRef = doc(db, "reservations", reservationId);
          await updateDoc(reservationRef, {
            order: orderItems,
            orderGrandTotal: subtotal.value + serviceFee.value,
            orderCreatedAt: new Date(),
          });

          console.log("Order saved to reservation successfully");
        }

        router.push("/choose-table");
      } catch (error) {
        console.error("Error saving order to reservation:", error);
        router.push("/choose-table");
      }
    } else {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        router.push("/login");
        return;
      }

      router.push("/checkout");
    }
  }
};
</script>
